import React from 'react';
import { Skeleton } from "src/components/ui/skeleton";

interface TableColumn {
  key: string;
  label: string;
  selectable?: boolean;
}

interface TableRow {
  [key: string]: string | number;
}

interface TableMetricCardProps {
  title: string;
  data: TableRow[];
  columns: TableColumn[];
  isLoading: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  formatValue?: (value: string | number, columnKey: string) => string;
  spanCols?: number;
}

const TableMetricCard: React.FC<TableMetricCardProps> = ({
  title,
  data,
  columns,
  isLoading,
  sortBy,
  sortOrder = 'desc',
  formatValue,
  spanCols,
}) => {
  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    } else {
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
  };

  const defaultFormatValue = (value: string | number, columnKey: string): string => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    if (typeof value === 'number') {
      return formatNumber(value);
    }
    return value.toString();
  };

  const valueFormatter = formatValue || defaultFormatValue;

  const sortedData = React.useMemo(() => {
    if (!sortBy || !data.length) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortBy];
      const bValue = b[sortBy];
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortOrder === 'desc' ? bValue - aValue : aValue - bValue;
      }

      const aStr = aValue.toString();
      const bStr = bValue.toString();
      return sortOrder === 'desc' ? bStr.localeCompare(aStr) : aStr.localeCompare(bStr);
    });
  }, [data, sortBy, sortOrder]);

  return (
    <div
      className={`rounded-lg bg-white border border-gray-400 p-4 flex-1 h-[200px] flex flex-col ${
        spanCols ? `sm:col-span-${spanCols} lg:col-span-${spanCols} xl:col-span-${spanCols} 2xl:col-span-${spanCols}` : ''
      }`}
    >
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-sm font-medium text-gray-600">
          {title}
        </h3>
        {columns.length > 1 && (
          <span className="text-xs font-medium text-gray-500 select-none">
            {columns[1].label}
          </span>
        )}
      </div>
      {isLoading ? (
        <div className="flex-1 flex flex-col min-h-0">
          <div className="space-y-1 overflow-y-auto flex-1 pr-2">
            {[...Array(5)].map((_, index) => (
              <div
                key={index}
                className="py-[0.75px]"
              >
                <Skeleton className="h-5 w-full" />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0">
          {sortedData.length > 0 ? (
            <div className="space-y-1 overflow-y-auto flex-1 pr-2">
              {sortedData.map((row, index) => (
                <div
                  key={index}
                  className="flex justify-between items-center py-[0.75px]"
                >
                  {columns.map((column, colIndex) => (
                    <span
                      key={column.key}
                      className={`text-sm ${
                        colIndex === 0
                          ? 'font-medium text-gray-900 truncate flex-1 min-w-0'
                          : 'text-gray-600 select-none flex-shrink-0 ml-2'
                      }`}
                      title={colIndex === 0 ? valueFormatter(row[column.key], column.key) : undefined}
                    >
                      {valueFormatter(row[column.key], column.key)}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-500 text-center py-4">
              No data available
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TableMetricCard;